import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {Label} from '@/components/ui/label/Label'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectTrigger,
    SelectVirtualList
} from '@/components/ui/select-atoms/SelectAtoms'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {useTranslation} from '@/translations/i18n'
import {Municipality} from '@/types'
import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {useRef} from 'react'
import {useGetMunicipalities} from '@/services/useGetMunicipalities'

interface InputSelectMunicipality {
    label: string
    value?: Municipality | null
    onChange: (value: Municipality | null) => void
    placeholder?: string
    errorMessage?: string
    isPortal?: boolean
}

export const InputSelectMunicipality = ({
    label,
    placeholder,
    value,
    onChange,
    errorMessage,
    isPortal
}: InputSelectMunicipality) => {
    const {t} = useTranslation()
    const inputAslRef = useRef(null)

    const {
        remappedData: municipalities,
        onSearch,
        onResetSearch,
        isFetching,
        hasNextPage,
        fetchNextPage,
        isError
    } = useGetMunicipalities({limit: 20})

    return (
        <ComboboxProvider setValue={onSearch} resetValueOnHide>
            <SelectProvider
                value={value?.id ?? ''}
                setOpen={() => onResetSearch(inputAslRef)}
                setValue={value =>
                    municipalities && onChange(municipalities.find(municipality => value == municipality.id) ?? null)
                }
            >
                <Flexbox direction="column" gap={1.5} fullWidth>
                    <Label>{label}</Label>
                    <Flexbox direction="column" gap={1.5} fullWidth>
                        <SelectTrigger
                            placeholder={placeholder}
                            fullWidth
                            value={value?.id}
                            hasError={!!errorMessage}
                            displayValue={() => value?.name}
                        />
                    </Flexbox>

                    <Flexbox align="center" gap={errorMessage ? 1.5 : 0}>
                        <InputStatusIcon hasError={!!errorMessage} />
                        <InputHelpText error={errorMessage} />
                    </Flexbox>

                    <SelectComboboxPopover portal={!!isPortal}>
                        <SelectComboboxInput ref={inputAslRef} placeholder={t('commons:search_placeholder')} />
                        <SelectComboboxList>
                            {isFetching ? (
                                <>
                                    {Array.from({length: 6}, (_, index) => (
                                        <Skeleton key={index} height={32} />
                                    ))}
                                </>
                            ) : isError ? (
                                <InputHelpText error={t('errors:default')} />
                            ) : municipalities && municipalities.length > 0 ? (
                                <SelectVirtualList
                                    data={municipalities}
                                    totalCount={municipalities.length}
                                    overscan={100}
                                    endReached={() => hasNextPage && fetchNextPage()}
                                    itemContent={(itemIndex, item) => (
                                        <SelectItem hasItemCheckbox key={itemIndex} value={item.id}>
                                            {item.name}
                                        </SelectItem>
                                    )}
                                />
                            ) : (
                                <InputHelpText helpText={t('commons:no_results')} />
                            )}
                        </SelectComboboxList>
                    </SelectComboboxPopover>
                </Flexbox>
            </SelectProvider>
        </ComboboxProvider>
    )
}
